import { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Plus, Minus, ArrowRight } from 'lucide-react';
import { useSeo, SITE_NAME } from '@/lib/seo';
import { Section } from '@/components/layout/Section';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Button } from '@/components/ui/button';
import { CategoryImage } from '@/components/FeaturedImage';
import { SERVICE_CARDS } from '@/lib/site';
import { Link } from 'react-router-dom';
import { TEST_IDS } from '@/constants/testIds/floreal';

export default function Servizi() {
    useSeo({
        title: `${SITE_NAME} — Servizi`,
        description: 'Servizi Floreal: matrimoni, eventi privati e aziendali, bouquet, piante, workshop floreali, consegne a domicilio.',
    });

    useEffect(() => {
        // Scroll to hash on load if present
        if (window.location.hash) {
            const id = window.location.hash.replace('#', '');
            const el = document.getElementById(id);
            if (el) setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
        }
    }, []);

    return (
        <>
            <section className="pt-32 sm:pt-40 pb-10 sm:pb-16">
                <div className="container-atelier max-w-3xl">
                    <p className="eyebrow mb-4">Servizi</p>
                    <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
                        Composizioni, allestimenti, esperienze.
                    </h1>
                    <p className="mt-6 text-muted-foreground leading-relaxed text-base sm:text-lg">
                        Ogni servizio nasce da una consulenza dedicata. Raccontaci la tua occasione: penseremo insieme allo stile, ai fiori, alla scenografia.
                    </p>
                </div>
            </section>

            <Section className="py-10 sm:py-14" data-testid={TEST_IDS.serviziGrid}>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
                    {SERVICE_CARDS.map((s, i) => (
                        <motion.article
                            key={s.id}
                            id={s.id}
                            initial={{ opacity: 0, y: 16 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: '-60px' }}
                            transition={{ duration: 0.4, delay: (i % 3) * 0.06, ease: [0.16, 1, 0.3, 1] }}
                            data-testid={`servizi-card-${s.id}`}
                            className="group rounded-xl border border-border bg-card overflow-hidden flex flex-col"
                        >
                            <CategoryImage
                                category={s.category}
                                alt={s.title}
                                className="aspect-[5/4]"
                                imgClassName="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
                                fallbackLabel={s.title}
                            />
                            <div className="p-6 flex flex-col flex-1">
                                <h3 className="font-serif text-2xl tracking-tight">{s.title}</h3>
                                <p className="mt-2 text-sm text-muted-foreground leading-relaxed flex-1">{s.excerpt}</p>
                                {s.details && (
                                    <Accordion type="single" collapsible className="mt-3">
                                        <AccordionItem value="details" className="border-b-0">
                                            <AccordionTrigger className="text-sm hover:no-underline py-2 [&[data-state=open]>svg]:hidden flex justify-start gap-2 [&>svg]:hidden">
                                                <span className="inline-flex items-center gap-1.5">
                                                    Vedi dettagli
                                                    <Plus className="h-3.5 w-3.5 transition-transform group-data-[state=open]:rotate-45" />
                                                </span>
                                            </AccordionTrigger>
                                            <AccordionContent>
                                                <ul className="text-sm text-muted-foreground leading-relaxed list-none space-y-1.5">
                                                    {s.details.map((d) => (
                                                        <li key={d} className="flex items-start gap-2">
                                                            <span className="mt-2 h-[3px] w-[3px] rounded-full bg-atelier-bronze-soft shrink-0" />
                                                            {d}
                                                        </li>
                                                    ))}
                                                </ul>
                                            </AccordionContent>
                                        </AccordionItem>
                                    </Accordion>
                                )}
                                {(s.id === 'matrimoni' || s.id === 'workshop' || s.id === 'piante-giardini') && (
                                    <Link
                                        to={s.id === 'matrimoni' ? '/matrimoni' : s.id === 'workshop' ? '/workshop' : '/giardini'}
                                        className="mt-4 inline-flex items-center gap-1.5 text-sm text-foreground hover:text-atelier-bronze transition-colors font-medium"
                                    >
                                        Scopri di più <ArrowRight className="h-3.5 w-3.5" />
                                    </Link>
                                )}
                                <Link
                                    to="/prenota"
                                    state={{ requestType: s.id === 'matrimoni' ? 'matrimonio' : s.id === 'eventi-privati' ? 'evento_privato' : s.id === 'eventi-aziendali' ? 'evento_aziendale' : s.id === 'workshop' ? 'workshop_privato' : 'altro' }}
                                    className="mt-3 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-atelier-bronze transition-colors"
                                >
                                    Richiedi consulenza <ArrowRight className="h-3.5 w-3.5" />
                                </Link>
                            </div>
                        </motion.article>
                    ))}
                </div>
            </Section>

            <Section className="py-10 sm:py-14">
                <div className="rounded-xl bg-atelier-beige/60 px-6 sm:px-10 py-10 sm:py-14 grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                    <div>
                        <p className="eyebrow mb-3">Hai un progetto?</p>
                        <h3 className="font-serif text-3xl sm:text-4xl leading-tight">Parlane con noi in atelier.</h3>
                    </div>
                    <div className="flex flex-wrap gap-3 lg:justify-end">
                        <Button asChild size="lg" className="h-12 px-6"><Link to="/prenota">Prenota un appuntamento</Link></Button>
                        <Button asChild size="lg" variant="outline" className="h-12 px-6 border-foreground/20 bg-background"><Link to="/contatti">Contattaci</Link></Button>
                    </div>
                </div>
            </Section>
        </>
    );
}
